import React, { useCallback } from 'react';
import { Box, Button, Tooltip, IconButton } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import TimelineIcon from '@mui/icons-material/Timeline';
import { useNavigate } from 'react-router-dom';
import useTouchInteraction from '../../../hooks/useTouchInteraction';
import { shouldEnableOptionChain } from '../../../services/utilities';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';

// Helper function to check if instrument is an index
const isIndex = (instrument) => {
  return instrument?.index === true;
};

const TouchableIconButton = React.memo(({ onClick, children, title, color, sx }) => {
  const { handlers, touchStates } = useTouchInteraction(onClick, true);

  return (
    <Tooltip title={title}>
      <IconButton
        {...handlers}
        size="small"
        sx={{
          ...sx,
          backgroundColor: touchStates.isActive && !touchStates.isTouchMoved
            ? 'action.hover'
            : undefined,
          transition: 'all 0.2s ease',
          WebkitTapHighlightColor: 'transparent',
          width: '28px',
          height: '28px',
          color: color,
          '&:hover': {
            backgroundColor: 'action.hover',
            transform: 'translateY(-1px)',
            boxShadow: '0 2px 5px rgba(0,0,0,0.1)'
          }
        }}
      >
        {children}
      </IconButton>
    </Tooltip>
  );
});

const DesktopActions = ({
  instrument,
  isExpanded,
  onBuy,
  onSell,
  onMarketDepth,
  onDelete,
  onMore,
  onSaveToMarketwatch,
  isInMarketwatch,
  userSettings
}) => {
  const navigate = useNavigate();

  const handleOptionChainClick = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (instrument?.exchange && instrument?.token) {
      navigate(`/overview/tdv/${instrument.exchange}/${instrument.token}`, {
        state: { defaultTab: 'chart' }
      });
    }
  }, [instrument?.exchange, instrument?.token, navigate]);

  const handleDelete = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    onDelete(e);
  }, [onDelete]);

  const handleMarketDepth = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    onMarketDepth(e);
  }, [onMarketDepth]);

  const isInstrumentIndex = isIndex(instrument);
  console.log("instrument",instrument);
  return (
    <Box
      onClick={(e) => e.stopPropagation()}
      sx={{
        position: 'absolute',
        right: 8,
        top: isExpanded ? '20px' : '50%',
        transform: 'translateY(-50%)',
        display: 'flex',
        gap: 0.5,
        backgroundColor: 'background.paper',
        borderRadius: '20px',
        boxShadow: '0 3px 10px rgba(0,0,0,0.08)',
        padding: '6px 10px',
        zIndex: 1,
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          boxShadow: '0 6px 15px rgba(0,0,0,0.1)',
          transform: 'translateY(-52%)',
        },
        pointerEvents: 'auto'
      }}
    >
      {!isInstrumentIndex && (
        <Button
          size="small"
          variant="contained"
          onClick={(e) => {
            e.stopPropagation();
            onBuy(instrument, e,userSettings);
          }}
          disabled={!instrument?.tradable}
          sx={{
            backgroundColor: !instrument?.tradable ? 'text.disabled' : 'success.main',
            '&:hover': {
              backgroundColor: !instrument?.tradable ? 'text.disabled' : 'success.dark',
              transform: !instrument?.tradable ? 'none' : 'translateY(-1px)',
              boxShadow: !instrument?.tradable ? 'none' : '0 2px 5px rgba(0,0,0,0.2)'
            },
            minWidth: '64px',
            height: '28px',
            fontSize: '0.8rem',
            fontWeight: 600,
            borderRadius: '14px',
            transition: 'all 0.2s ease',
            textTransform: 'none',
            pointerEvents: !instrument?.tradable ? 'none' : 'auto'
          }}
        >
          Buy
        </Button>
      )}

      {!isInstrumentIndex && (
        <Button
          size="small"
          variant="contained"
          onClick={(e) => {
            e.stopPropagation();
            onSell(instrument, e,userSettings);
          }}
          disabled={!instrument?.tradable}
          sx={{
            backgroundColor: !instrument?.tradable ? 'text.disabled' : 'error.main',
            '&:hover': {
              backgroundColor: !instrument?.tradable ? 'text.disabled' : 'error.dark',
              transform: !instrument?.tradable ? 'none' : 'translateY(-1px)',
              boxShadow: !instrument?.tradable ? 'none' : '0 2px 5px rgba(0,0,0,0.2)'
            },
            minWidth: '64px',
            height: '28px',
            fontSize: '0.8rem',
            fontWeight: 600,
            borderRadius: '14px',
            transition: 'all 0.2s ease',
            textTransform: 'none',
            pointerEvents: !instrument?.tradable ? 'none' : 'auto'
          }}
        >
          Sell
        </Button>
      )}

      <Box
        onClick={handleOptionChainClick}
        sx={{
          display: 'flex',
          pointerEvents: 'auto'
        }}
      >
        <TouchableIconButton
          onClick={handleOptionChainClick}
          title={shouldEnableOptionChain(instrument) ? "Option Chain + Charts" : "Charts"}
          color="primary.main"
        >
          <TimelineIcon fontSize="small" />
        </TouchableIconButton>
      </Box>

      <Box
        onClick={handleMarketDepth}
        sx={{
          display: 'flex',
          pointerEvents: 'auto'
        }}
      >
        <TouchableIconButton
          onClick={handleMarketDepth}
          title="Market Depth"
          color={isExpanded ? 'primary.main' : 'text.secondary'}
          sx={{
            backgroundColor: isExpanded ? 'action.selected' : 'transparent'
          }}
        >
          {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </TouchableIconButton>
      </Box>

      <Box
        onClick={handleDelete}
        sx={{
          display: 'flex',
          pointerEvents: 'auto'
        }}
      >
        <TouchableIconButton
          onClick={handleDelete}
          title="Delete"
          color="error.main"
        >
          <DeleteIcon fontSize="small" />
        </TouchableIconButton>
      </Box>  
    </Box>
  );
};

export default DesktopActions; 